import type { DateTime } from 'luxon'
import type { Descendant, Element, Text } from 'slate'

import type { SerializedRichText } from './types'

type ComposeChildren = Descendant | string
type AlignOptions = { align?: 'left' | 'center' | 'right' }

function normalizeChildren(children: ComposeChildren[]): Descendant[] {
  return children.map(child => {
    if (typeof child === 'string') {
      return {
        text: child,
      }
    }
    return child
  })
}
function normalizeParameters<Options>(
  optionsOrFirstChild: Options | ComposeChildren | null,
  children: ComposeChildren[]
): {
  options: Partial<Options> | null
  children: Descendant[]
} {
  if (
    optionsOrFirstChild != null &&
    // This is supporting the string part of ComposeChildren
    (typeof optionsOrFirstChild === 'string' ||
      // This is supporting the Descendant part of ComposeChildren
      (typeof optionsOrFirstChild === 'object' &&
        (('text' in optionsOrFirstChild && typeof optionsOrFirstChild.text === 'string') ||
          ('type' in optionsOrFirstChild && typeof optionsOrFirstChild.type === 'string') ||
          ('children' in optionsOrFirstChild && Array.isArray(optionsOrFirstChild.children)))))
  ) {
    return {
      options: {},
      children: normalizeChildren([optionsOrFirstChild as Descendant, ...children]),
    }
  }

  return {
    options: optionsOrFirstChild as Options,
    children: normalizeChildren(children),
  }
}

// NOTE: Remember to update ./validation.tsx to update zod schema if you update the composers here
const context = {
  text(
    text: string,
    options: { bold?: boolean; italic?: boolean; underline?: boolean } = {}
  ): Text {
    return {
      text,
      ...options,
    }
  },
  paragraph(
    optionsOrFirstChild?: AlignOptions | ComposeChildren | null,
    ...children: ComposeChildren[]
  ): Element & { type: 'paragraph' } {
    const { options, children: normalizedChildren } = normalizeParameters(
      optionsOrFirstChild,
      children
    )

    return {
      type: 'paragraph',
      ...options,
      children: normalizedChildren,
    }
  },
  heading(
    optionsOrFirstChild?: AlignOptions | ComposeChildren | null,
    ...children: ComposeChildren[]
  ): Element & { type: 'heading' } {
    const { options, children: normalizedChildren } = normalizeParameters(
      optionsOrFirstChild,
      children
    )

    return {
      type: 'heading',
      ...options,
      children: normalizedChildren,
    }
  },
  headingThree(
    optionsOrFirstChild?: AlignOptions | ComposeChildren | null,
    ...children: ComposeChildren[]
  ): Element & { type: 'heading-three' } {
    const { options, children: normalizedChildren } = normalizeParameters(
      optionsOrFirstChild,
      children
    )

    return {
      type: 'heading-three',
      ...options,
      children: normalizedChildren,
    }
  },
  bulletedList(
    optionsOrFirstChild?: AlignOptions | ComposeChildren | null,
    ...children: ComposeChildren[]
  ): Element & { type: 'bulleted-list' } {
    const { options, children: normalizedChildren } = normalizeParameters(
      optionsOrFirstChild,
      children
    )

    return {
      type: 'bulleted-list',
      ...options,
      children: normalizedChildren,
    }
  },
  numberedList(
    optionsOrFirstChild?: AlignOptions | ComposeChildren | null,
    ...children: ComposeChildren[]
  ): Element & { type: 'numbered-list' } {
    const { options, children: normalizedChildren } = normalizeParameters(
      optionsOrFirstChild,
      children
    )

    return {
      type: 'numbered-list',
      ...options,
      children: normalizedChildren,
    }
  },
  listItem(
    optionsOrFirstChild?: AlignOptions | ComposeChildren | null,
    ...children: ComposeChildren[]
  ): Element & { type: 'list-item' } {
    const { options, children: normalizedChildren } = normalizeParameters(
      optionsOrFirstChild,
      children
    )

    return {
      type: 'list-item',
      ...options,
      children: normalizedChildren,
    }
  },
  link(
    options: { url: string; isInternal?: boolean },
    ...children: ComposeChildren[]
  ): Element & { type: 'link' } {
    return {
      type: 'link',
      ...options,
      children: normalizeChildren(children),
    }
  },
  linkInternal(url: string, ...children: ComposeChildren[]): Element & { type: 'link' } {
    return context.link({ url, isInternal: true }, ...children)
  },
  linkExternal(url: string, ...children: ComposeChildren[]): Element & { type: 'link' } {
    return context.link({ url, isInternal: false }, ...children)
  },
  math(
    { latex, displayMode }: { latex: string; displayMode: boolean },
    ...children: ComposeChildren[]
  ): Element & { type: 'math' } {
    return {
      type: 'math',
      latex,
      displayMode,
      children: normalizeChildren(children),
    }
  },
  image(url: string, ...children: ComposeChildren[]): Element & { type: 'image' } {
    return {
      type: 'image',
      url,
      children: normalizeChildren(children),
    }
  },
  template(readOnlyText: string, ...children: ComposeChildren[]): Element & { type: 'template' } {
    return {
      type: 'template',
      readOnlyText,
      children: normalizeChildren(children),
    }
  },
  mention(
    { displayName, userId }: { displayName: string; userId: number },
    ...children: ComposeChildren[]
  ): Element & { type: 'mention' } {
    return {
      type: 'mention',
      displayName,
      userId,
      children: normalizeChildren(children),
    }
  },
  horizontalLine(...children: ComposeChildren[]): Element & { type: 'horizontal-line' } {
    return {
      type: 'horizontal-line',
      children: normalizeChildren(children),
    }
  },
  blockquote(...children: ComposeChildren[]): Element & { type: 'blockquote' } {
    return {
      type: 'blockquote',
      children: normalizeChildren(children),
    }
  },
  sup(...children: ComposeChildren[]): Element & { type: 'sup' } {
    return {
      type: 'sup',
      children: normalizeChildren(children),
    }
  },
  dateTime(
    dateTime: DateTime,
    options: { format?: string; isRelative?: boolean }
  ): Element & { type: 'date-time' } {
    return {
      type: 'date-time',
      ...options,
      dateTimeISO: dateTime.toISO(),
      children: [{ text: '' }],
    }
  },
}
// NOTE: Remember to update ./validation.tsx to update zod schema if you update the composers here

export type RichTextComposeContext = typeof context

export default function composeRichText(
  callback: (context: RichTextComposeContext) => Descendant | Descendant[] | string
): SerializedRichText {
  const value = callback(context)
  if (typeof value === 'string') {
    return {
      serialization_method: 'plain_text',
      content: value,
    }
  }
  return {
    serialization_method: 'slate_json_v1',
    content: Array.isArray(value) ? value : [value],
  }
}
